import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useEmployeeTaxasHooks } from './hooks';
import { EmployeeTaxaFormDialog } from './EmployeeTaxaFormDialog';
import type { EmployeeTaxaDto, EmployeeTaxaListItem } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataVigencia',
  sortOrder: 'desc',
};

export interface EmployeeTaxasManagerProps {
  employeeId: number;
}

/** Formata decimal-string ou number em `R$ X.XXX,XX`. */
function formatBRL(v: string | number): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Formata `YYYY-MM-DD` → `DD/MM/YYYY`. */
function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

/**
 * Núcleo reusável do CRUD de Taxas de um Colaborador. Sem decoração externa —
 * embebível em `EmployeeTaxasDialog` ou em uma `EmployeeTaxasPage` futura.
 * Espelha `CompanyAddressesManager`.
 *
 * PK do filho é `dataVigencia` (Date string), por isso o id é string e o
 * grid usa `getRowId={(row) => row.dataVigencia}`.
 */
export function EmployeeTaxasManager({ employeeId }: EmployeeTaxasManagerProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('colaborador-taxas', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<EmployeeTaxaDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<EmployeeTaxaListItem | null>(null);

  const hooks = useEmployeeTaxasHooks(employeeId);
  const listQuery = hooks.useList(query);
  const deleteMut = hooks.useDelete();

  async function openEdit(row: EmployeeTaxaListItem): Promise<void> {
    try {
      setEditing(await hooks.api.get(row.dataVigencia));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.dataVigencia);
      toast.success(t('features.colaboradores.taxas.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<EmployeeTaxaListItem>[] = [
    {
      field: 'dataVigencia',
      headerName: t('features.colaboradores.taxas.columns.dataVigencia'),
      width: 140,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'tipoPagamento',
      headerName: t('features.colaboradores.taxas.columns.tipoPagamento'),
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const tp = params.row.tipoPagamento;
        if (!tp) return null;
        return (
          <Chip
            size="small"
            label={t(`features.colaboradores.taxas.tipoPagamento.${tp}`)}
            color={tp === 'F' ? 'primary' : 'default'}
            variant={tp === 'F' ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      field: 'valorHoraPagar',
      headerName: t('features.colaboradores.taxas.columns.valorHoraPagar'),
      width: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatBRL(params.row.valorHoraPagar),
    },
    {
      field: 'valorHoraReceber',
      headerName: t('features.colaboradores.taxas.columns.valorHoraReceber'),
      width: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatBRL(params.row.valorHoraReceber),
    },
    {
      field: 'valorFixoPagar',
      headerName: t('features.colaboradores.taxas.columns.valorFixoPagar'),
      width: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) =>
        params.row.tipoPagamento === 'F' ? formatBRL(params.row.valorFixoPagar) : '—',
    },
    {
      field: 'valorFixoReceber',
      headerName: t('features.colaboradores.taxas.columns.valorFixoReceber'),
      width: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) =>
        params.row.tipoPagamento === 'F' ? formatBRL(params.row.valorFixoReceber) : '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.colaboradores.taxas.actions.edit', {
                data: formatDate(params.row.dataVigencia),
              })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.colaboradores.taxas.actions.delete', {
                data: formatDate(params.row.dataVigencia),
              })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          {t('features.colaboradores.taxas.actions.new')}
        </Button>
      </Stack>

      <DataTable<EmployeeTaxaListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.colaboradores.taxas.searchPlaceholder')}
      />

      <EmployeeTaxaFormDialog
        open={editing !== null}
        taxa={editing === 'new' ? null : editing}
        hooks={{ useCreate: hooks.useCreate, useUpdate: hooks.useUpdate }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.colaboradores.taxas.deleteConfirm.title')}
        description={t('features.colaboradores.taxas.deleteConfirm.description', {
          data: pendingDelete ? formatDate(pendingDelete.dataVigencia) : '',
        })}
        confirmLabel={t('common.delete')}
        loading={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Stack>
  );
}
